import React, { useEffect, useCallback, useState } from 'react';
import { useJiraStore, JiraIssue, JiraSprint, SprintReport, VelocityData, BurnupData, DeploymentFrequencyData } from '../store/jiraStore';
import Select from './Select';

interface JiraPanelProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string | undefined;
  inline?: boolean;
}

type ActiveTab = 'issues' | 'report';

type ReportType = 'burndown' | 'burnup' | 'velocity' | 'cumulative_flow' | 'cycle_time' | 'deployment_frequency';

interface ReportDef {
  id: ReportType;
  name: string;
  desc: string;
  filterType: 'sprint' | 'date_range';
  implemented: boolean;
  Icon: React.FC;
}

const BurndownIcon: React.FC = () => (
  <svg viewBox="0 0 60 40" className="w-full h-full" fill="none">
    <rect x="0" y="8" width="22" height="24" fill="#f1f5f9" rx="2"/>
    <polyline points="0,8 15,14 30,22 45,32 60,36" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="0" y1="8" x2="60" y2="36" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="3 2"/>
    <polyline points="0,10 15,16 30,24 45,34 60,38" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BurnupIcon: React.FC = () => (
  <svg viewBox="0 0 60 40" className="w-full h-full" fill="none">
    <rect x="0" y="10" width="22" height="22" fill="#f1f5f9" rx="2"/>
    <line x1="0" y1="38" x2="60" y2="6" stroke="#475569" strokeWidth="1.5"/>
    <polyline points="0,38 15,32 30,24 45,16 60,10" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="0,36 15,30 30,22 45,14 60,8" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const VelocityIcon: React.FC = () => (
  <svg viewBox="0 0 64 44" className="w-full h-full" fill="none">
    {([
      [4,18,14,12],[14,12,22,8],[24,22,32,16],[34,10,42,7],[44,20,52,14],[54,28,60,20]
    ] as [number,number,number,number][]).map(([x1,h1,x2,h2], i) => (
      <g key={i}>
        <rect x={x1} y={44-h1} width="8" height={h1} fill={i===5?'#7c3aed':'#a78bfa'} rx="1"/>
        <rect x={x2} y={44-h2} width="7" height={h2} fill={i===5?'#c4b5fd':'#ddd6fe'} rx="1"/>
      </g>
    ))}
  </svg>
);

const CfdIcon: React.FC = () => (
  <svg viewBox="0 0 60 40" className="w-full h-full" fill="none">
    <path d="M0,38 L60,38 L60,30 L0,33 Z" fill="#86efac"/>
    <path d="M0,33 L60,30 L60,20 L0,25 Z" fill="#fde68a"/>
    <path d="M0,25 L60,20 L60,10 L0,15 Z" fill="#c4b5fd"/>
    <path d="M0,15 L60,10 L60,5 L0,7 Z" fill="#94a3b8"/>
  </svg>
);

const CycleTimeIcon: React.FC = () => (
  <svg viewBox="0 0 60 44" className="w-full h-full" fill="none">
    <line x1="4" y1="26" x2="56" y2="26" stroke="#e2e8f0" strokeWidth="1.5" strokeDasharray="3 2"/>
    {([[10,18],[18,26],[26,12],[34,22],[42,16],[52,28]] as [number,number][]).map(([x,y],i) => (
      <circle key={i} cx={x} cy={y} r="3.5" fill={y < 22 ? '#7c3aed' : '#94a3b8'}/>
    ))}
    <rect x="6" y="32" width="18" height="4" fill="#f1f5f9" rx="1"/>
    <rect x="6" y="32" width="12" height="4" fill="#fbbf24" rx="1" opacity="0.8"/>
    <rect x="6" y="38" width="26" height="4" fill="#f1f5f9" rx="1"/>
    <rect x="6" y="38" width="18" height="4" fill="#fbbf24" rx="1" opacity="0.5"/>
  </svg>
);

const DeploymentIcon: React.FC = () => (
  <svg viewBox="0 0 60 44" className="w-full h-full" fill="none">
    {([[4,18],[13,24],[22,20],[31,28],[40,22],[49,32]] as [number,number][]).map(([x,h],i) => (
      <rect key={i} x={x} y={44-h} width="7" height={h} fill={i===5?'#10b981':'#64748b'} rx="1"/>
    ))}
    <polyline points="7,32 16,24 24,28 33,20 42,26 53,16" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="53" cy="40" r="4" fill="white" stroke="#3b82f6" strokeWidth="2"/>
  </svg>
);

const REPORT_DEFS: ReportDef[] = [
  {
    id: 'burndown',
    name: 'Sprint burndown chart',
    desc: 'Track and manage the total work remaining within a sprint.',
    filterType: 'sprint',
    implemented: true,
    Icon: BurndownIcon,
  },
  {
    id: 'burnup',
    name: 'Burnup report',
    desc: 'Visualise completed work compared to the total sprint scope.',
    filterType: 'sprint',
    implemented: true,
    Icon: BurnupIcon,
  },
  {
    id: 'velocity',
    name: 'Velocity report',
    desc: 'Predict how much work your team can commit to in future sprints.',
    filterType: 'sprint',
    implemented: true,
    Icon: VelocityIcon,
  },
  {
    id: 'cumulative_flow',
    name: 'Cumulative flow diagram',
    desc: 'See which columns accumulate work items and identify bottlenecks.',
    filterType: 'date_range',
    implemented: false,
    Icon: CfdIcon,
  },
  {
    id: 'cycle_time',
    name: 'Cycle Time Report',
    desc: 'Understand how long it takes to ship items through the pipeline.',
    filterType: 'date_range',
    implemented: false,
    Icon: CycleTimeIcon,
  },
  {
    id: 'deployment_frequency',
    name: 'Deployment Frequency Report',
    desc: 'Understand how often you are shipping value to your customers.',
    filterType: 'sprint',
    implemented: true,
    Icon: DeploymentIcon,
  },
];

// ─── Shared Design Tokens ────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-slate-100 text-slate-600 border-slate-200',
  indeterminate: 'bg-orange-100 text-orange-700 border-orange-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const PRIORITY_COLORS: Record<string, string> = {
  Highest: 'text-red-500',
  High: 'text-orange-500',
  Medium: 'text-amber-500',
  Low: 'text-orange-400',
  Lowest: 'text-slate-400',
};

const ISSUE_TYPE_COLORS: Record<string, string> = {
  Bug: 'bg-red-100 text-red-700',
  Story: 'bg-blue-100 text-blue-700',
  Task: 'bg-violet-100 text-violet-700',
  Epic: 'bg-purple-100 text-purple-700',
  Subtask: 'bg-slate-100 text-slate-600',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const JiraLogo: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 32 32" fill="none">
    <path d="M16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16S24.837 0 16 0z" fill="#2684FF" />
    <path d="M23.647 8.353l-7.646 7.648-3.882-3.882-3.766 3.764 3.882 3.882 3.766 3.764 11.412-11.41-3.766-3.766z" fill="white" />
  </svg>
);

const PriorityIcon: React.FC<{ priority: string }> = ({ priority }) => (
  <span className={`text-xs font-bold ${PRIORITY_COLORS[priority] || 'text-slate-400'}`} title={priority}>
    {priority === 'Highest' || priority === 'High' ? '▲' : priority === 'Lowest' || priority === 'Low' ? '▼' : '●'}
  </span>
);

const IssueSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-3 px-4">
    {[1, 2, 3, 4].map(i => (
      <div key={i} className="bg-slate-100 rounded-lg p-3 space-y-2">
        <div className="flex gap-2">
          <div className="h-4 w-16 bg-slate-200 rounded" />
          <div className="h-4 flex-1 bg-slate-200 rounded" />
        </div>
        <div className="flex gap-2">
          <div className="h-3 w-20 bg-slate-200/80 rounded" />
          <div className="h-3 w-16 bg-slate-200/80 rounded" />
        </div>
      </div>
    ))}
  </div>
);

const ReportSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-4 px-4 pt-2">
    <div className="h-24 bg-slate-100 rounded-xl" />
    <div className="h-16 bg-slate-100 rounded-xl" />
    <div className="h-28 bg-slate-100 rounded-xl" />
    <div className="h-20 bg-slate-100 rounded-xl" />
  </div>
);

const IssueCard: React.FC<{ issue: JiraIssue; compact?: boolean }> = ({ issue, compact }) => {
  const statusColor = STATUS_COLORS[issue.status.category] || STATUS_COLORS['new'];
  const initials = issue.assignee?.display_name
    ? issue.assignee.display_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : null;

  return (
    <button
      onClick={() => window.open(issue.url, '_blank', 'noopener,noreferrer')}
      className="w-full text-left bg-white hover:bg-orange-50 border border-slate-200 hover:border-orange-300 rounded-lg p-3 transition-all duration-150 group"
    >
      <div className="flex items-start gap-2 mb-1.5">
        <span className="shrink-0 text-xs font-mono font-semibold text-orange-600 bg-orange-100 border border-orange-200 px-1.5 py-0.5 rounded">
          {issue.key}
        </span>
        <span className={`text-sm text-slate-700 leading-snug group-hover:text-slate-900 flex-1 ${compact ? 'line-clamp-1' : 'line-clamp-2'}`}>
          {issue.summary}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs px-1.5 py-0.5 rounded border ${statusColor}`}>
          {issue.status.name}
        </span>
        {issue.priority && <PriorityIcon priority={issue.priority} />}
        {initials && (
          <span className="ml-auto text-xs bg-slate-100 text-slate-600 rounded-full w-5 h-5 flex items-center justify-center font-medium shrink-0">
            {initials}
          </span>
        )}
      </div>
    </button>
  );
};

const SprintSelector: React.FC<{
  sprints: JiraSprint[];
  selectedSprintId: number | null;
  teamId: string;
  loading: boolean;
  onAfterSelect?: (sprintId: number | null) => void;
}> = ({ sprints, selectedSprintId, teamId, loading, onAfterSelect }) => {
  const { selectSprint } = useJiraStore();
  return (
    <div className="px-4 pb-3">
      <Select
        value={selectedSprintId != null ? String(selectedSprintId) : ''}
        onChange={(val) => {
          const id = val ? Number(val) : null;
          selectSprint(teamId, id);
          onAfterSelect?.(id);
        }}
        placeholder="All project issues"
        disabled={loading}
        options={sprints.map((s) => ({
          value: String(s.id),
          label: s.name + (s.state === 'active' ? ' (Active)' : s.state === 'closed' ? ' (Closed)' : ''),
        }))}
      />
    </div>
  );
};

// ─── Report Type Selector ─────────────────────────────────────────────────────

const ReportTypeGrid: React.FC<{
  onSelect: (type: ReportType) => void;
}> = ({ onSelect }) => (
  <div className="flex-1 overflow-y-auto px-4 py-4">
    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Choose a report</p>
    <div className="grid grid-cols-2 gap-3">
      {REPORT_DEFS.map(def => (
        <button
          key={def.id}
          onClick={() => def.implemented && onSelect(def.id)}
          disabled={!def.implemented}
          className={`relative text-left border rounded-xl p-3 transition-all duration-150 group focus:outline-none ${
            def.implemented
              ? 'bg-white border-slate-200 hover:border-orange-300 hover:shadow-md focus:ring-2 focus:ring-orange-400 cursor-pointer'
              : 'bg-slate-50 border-slate-100 cursor-not-allowed opacity-60'
          }`}
        >
          {!def.implemented && (
            <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wide bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full leading-none">
              Soon
            </span>
          )}
          <div className="w-full h-[52px] mb-2.5 rounded-lg bg-slate-50 overflow-hidden p-1.5">
            <def.Icon />
          </div>
          <p className={`text-xs font-semibold leading-snug transition-colors pr-4 ${
            def.implemented ? 'text-slate-800 group-hover:text-orange-600' : 'text-slate-400'
          }`}>
            {def.name}
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5 leading-tight line-clamp-2">
            {def.desc}
          </p>
          <span className={`mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
            def.filterType === 'sprint'
              ? 'bg-orange-50 text-orange-600'
              : 'bg-blue-50 text-blue-600'
          }`}>
            {def.filterType === 'sprint' ? '⚡ Sprint' : '📅 Date range'}
          </span>
        </button>
      ))}
    </div>
  </div>
);

// ─── Date Range Filter ────────────────────────────────────────────────────────

const DateRangeFilter: React.FC<{
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
}> = ({ startDate, endDate, onChange }) => (
  <div className="px-4 pb-3 flex items-center gap-2">
    <input
      type="date"
      value={startDate}
      onChange={e => onChange(e.target.value, endDate)}
      className="flex-1 bg-white border border-slate-200 text-slate-700 text-sm rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
    />
    <span className="text-slate-400 text-xs shrink-0">→</span>
    <input
      type="date"
      value={endDate}
      onChange={e => onChange(startDate, e.target.value)}
      className="flex-1 bg-white border border-slate-200 text-slate-700 text-sm rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
    />
  </div>
);

// ─── Coming Soon Placeholder ──────────────────────────────────────────────────

const ComingSoonPlaceholder: React.FC<{ reportName: string }> = ({ reportName }) => (
  <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3 pb-12">
    <div className="w-12 h-12 bg-violet-50 border border-violet-100 rounded-xl flex items-center justify-center">
      <svg className="w-6 h-6 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    </div>
    <p className="text-slate-700 text-sm font-semibold">{reportName}</p>
    <p className="text-slate-400 text-xs">This report is coming soon.</p>
    <span className="text-[11px] font-bold uppercase tracking-wide bg-violet-100 text-violet-600 px-2.5 py-1 rounded-full">
      Coming Soon
    </span>
  </div>
);

// ─── SVG Chart Primitives ─────────────────────────────────────────────────────

const ChartSkeleton: React.FC = () => (
  <div className="animate-pulse flex-1 flex flex-col gap-3 px-4 py-4">
    <div className="h-4 w-32 bg-slate-100 rounded" />
    <div className="flex-1 min-h-[180px] bg-slate-100 rounded-xl" />
  </div>
);

// ─── Velocity Chart ───────────────────────────────────────────────────────────

const VelocityReport: React.FC<{ data: VelocityData }> = ({ data }) => {
  const sprints = data.sprints;
  if (!sprints.length) {
    return <div className="text-center text-slate-400 text-sm py-10">No sprint data available.</div>;
  }

  const W = 340;
  const H = 180;
  const PAD = { top: 16, right: 12, bottom: 48, left: 32 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const maxVal = Math.max(...sprints.map(s => s.total), 1);
  const barGroupW = chartW / sprints.length;
  const barW = Math.min(barGroupW * 0.35, 20);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Issue Velocity</p>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 220 }}>
          {/* Y-axis gridlines */}
          {[0, 0.25, 0.5, 0.75, 1].map(pct => {
            const y = PAD.top + chartH * (1 - pct);
            const val = Math.round(maxVal * pct);
            return (
              <g key={pct}>
                <line x1={PAD.left} x2={PAD.left + chartW} y1={y} y2={y} stroke="#f1f5f9" strokeWidth="1" />
                <text x={PAD.left - 4} y={y + 3.5} textAnchor="end" fontSize="8" fill="#94a3b8">{val}</text>
              </g>
            );
          })}

          {sprints.map((s, i) => {
            const cx = PAD.left + barGroupW * i + barGroupW / 2;
            const totalH = (s.total / maxVal) * chartH;
            const doneH = (s.done / maxVal) * chartH;
            const label = s.name.replace(/sprint\s*/i, 'S').slice(0, 8);
            return (
              <g key={s.id}>
                {/* total bar (background) */}
                <rect
                  x={cx - barW - 1}
                  y={PAD.top + chartH - totalH}
                  width={barW}
                  height={totalH}
                  fill="#e2e8f0"
                  rx="2"
                />
                {/* done bar */}
                <rect
                  x={cx + 1}
                  y={PAD.top + chartH - doneH}
                  width={barW}
                  height={doneH}
                  fill="#7c3aed"
                  rx="2"
                />
                {/* label */}
                <text
                  x={cx}
                  y={PAD.top + chartH + 12}
                  textAnchor="middle"
                  fontSize="7.5"
                  fill="#64748b"
                >
                  {label}
                </text>
                {/* done count */}
                {doneH > 10 && (
                  <text
                    x={cx + 1 + barW / 2}
                    y={PAD.top + chartH - doneH - 3}
                    textAnchor="middle"
                    fontSize="7"
                    fill="#7c3aed"
                    fontWeight="600"
                  >
                    {s.done}
                  </text>
                )}
              </g>
            );
          })}

          {/* X-axis baseline */}
          <line x1={PAD.left} x2={PAD.left + chartW} y1={PAD.top + chartH} y2={PAD.top + chartH} stroke="#e2e8f0" strokeWidth="1" />
        </svg>
      </div>
      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 px-1">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-slate-200" />
          <span className="text-xs text-slate-500">Total</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-violet-600" />
          <span className="text-xs text-slate-500">Done</span>
        </div>
      </div>
    </div>
  );
};

// ─── Burnup Chart ─────────────────────────────────────────────────────────────

const BurnupReport: React.FC<{ data: BurnupData }> = ({ data }) => {
  const { days, total } = data;
  if (!days.length) {
    return <div className="text-center text-slate-400 text-sm py-10">No burnup data available for this sprint.</div>;
  }

  const W = 340;
  const H = 180;
  const PAD = { top: 16, right: 12, bottom: 36, left: 32 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const toX = (i: number) => PAD.left + (i / (days.length - 1 || 1)) * chartW;
  const toY = (v: number) => PAD.top + chartH - (v / (total || 1)) * chartH;

  const idealPoints = days.map((d, i) => `${toX(i)},${toY(d.ideal)}`).join(' ');
  const actualPoints = days.map((d, i) => `${toX(i)},${toY(d.actual)}`).join(' ');

  const tickStep = Math.max(1, Math.ceil(days.length / 6));
  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Burnup Chart</p>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 220 }}>
          {/* Y gridlines */}
          {yTicks.map(pct => {
            const y = PAD.top + chartH * (1 - pct);
            return (
              <g key={pct}>
                <line x1={PAD.left} x2={PAD.left + chartW} y1={y} y2={y} stroke="#f1f5f9" strokeWidth="1" />
                <text x={PAD.left - 4} y={y + 3.5} textAnchor="end" fontSize="8" fill="#94a3b8">
                  {Math.round(total * pct)}
                </text>
              </g>
            );
          })}

          {/* Scope line (total) */}
          <line
            x1={PAD.left} x2={PAD.left + chartW}
            y1={PAD.top} y2={PAD.top}
            stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 2"
          />

          {/* Ideal line */}
          <polyline points={idealPoints} fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="4 2" />

          {/* Actual line */}
          <polyline points={actualPoints} fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

          {/* X-axis ticks */}
          {days.map((d, i) => {
            if (i % tickStep !== 0 && i !== days.length - 1) return null;
            return (
              <text key={i} x={toX(i)} y={PAD.top + chartH + 12} textAnchor="middle" fontSize="7.5" fill="#94a3b8">
                D{d.day}
              </text>
            );
          })}

          {/* Baseline */}
          <line x1={PAD.left} x2={PAD.left + chartW} y1={PAD.top + chartH} y2={PAD.top + chartH} stroke="#e2e8f0" strokeWidth="1" />
        </svg>
      </div>
      <div className="flex items-center gap-4 mt-2 px-1">
        <div className="flex items-center gap-1.5">
          <div className="w-5 border-t border-dashed border-slate-300" />
          <span className="text-xs text-slate-500">Ideal</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-5 border-t-2 border-violet-600" />
          <span className="text-xs text-slate-500">Actual</span>
        </div>
        <span className="ml-auto text-xs text-slate-400">{days[days.length - 1]?.actual ?? 0}/{total} done</span>
      </div>
    </div>
  );
};

// ─── Deployment Frequency Chart ───────────────────────────────────────────────

const DeploymentFrequencyReport: React.FC<{ data: DeploymentFrequencyData }> = ({ data }) => {
  const { days, total_closed } = data;
  if (!days.length) {
    return <div className="text-center text-slate-400 text-sm py-10">No issues closed in this sprint yet.</div>;
  }

  const W = 340;
  const H = 160;
  const PAD = { top: 16, right: 12, bottom: 40, left: 32 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const maxVal = Math.max(...days.map(d => d.closed), 1);
  const barW = Math.min(chartW / days.length * 0.65, 28);

  const avgPerDay = total_closed / days.length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Deployment Frequency</p>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-base font-bold text-slate-800">{total_closed}</p>
            <p className="text-[10px] text-slate-400">total closed</p>
          </div>
          <div className="text-right">
            <p className="text-base font-bold text-emerald-600">{avgPerDay.toFixed(1)}</p>
            <p className="text-[10px] text-slate-400">avg/day</p>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 200 }}>
          {/* Y gridlines */}
          {[0, 0.5, 1].map(pct => {
            const y = PAD.top + chartH * (1 - pct);
            return (
              <g key={pct}>
                <line x1={PAD.left} x2={PAD.left + chartW} y1={y} y2={y} stroke="#f1f5f9" strokeWidth="1" />
                <text x={PAD.left - 4} y={y + 3.5} textAnchor="end" fontSize="8" fill="#94a3b8">
                  {Math.round(maxVal * pct)}
                </text>
              </g>
            );
          })}

          {/* Average line */}
          {(() => {
            const avgY = PAD.top + chartH - (avgPerDay / maxVal) * chartH;
            return <line x1={PAD.left} x2={PAD.left + chartW} y1={avgY} y2={avgY} stroke="#f59e0b" strokeWidth="1" strokeDasharray="3 2" />;
          })()}

          {days.map((d, i) => {
            const barH = (d.closed / maxVal) * chartH;
            const cx = PAD.left + (chartW / days.length) * i + (chartW / days.length) / 2;
            const shortDate = d.date.slice(5); // MM-DD
            return (
              <g key={d.date}>
                <rect
                  x={cx - barW / 2}
                  y={PAD.top + chartH - barH}
                  width={barW}
                  height={barH}
                  fill="#10b981"
                  rx="2"
                />
                {barH > 14 && (
                  <text x={cx} y={PAD.top + chartH - barH - 3} textAnchor="middle" fontSize="7" fill="#10b981" fontWeight="600">
                    {d.closed}
                  </text>
                )}
                {days.length <= 14 && (
                  <text x={cx} y={PAD.top + chartH + 12} textAnchor="middle" fontSize="7" fill="#94a3b8" transform={`rotate(-30, ${cx}, ${PAD.top + chartH + 12})`}>
                    {shortDate}
                  </text>
                )}
              </g>
            );
          })}

          <line x1={PAD.left} x2={PAD.left + chartW} y1={PAD.top + chartH} y2={PAD.top + chartH} stroke="#e2e8f0" strokeWidth="1" />
        </svg>
      </div>
      <div className="flex items-center gap-1.5 mt-1 px-1">
        <div className="w-5 border-t border-dashed border-amber-400" />
        <span className="text-xs text-slate-400">Daily average</span>
      </div>
    </div>
  );
};

// ─── Report Components ────────────────────────────────────────────────────────

const CompletionRing: React.FC<{ rate: number }> = ({ rate }) => {
  const r = 26;
  const circ = 2 * Math.PI * r;
  const offset = circ - (rate / 100) * circ;
  const color = rate >= 80 ? '#10b981' : rate >= 50 ? '#f59e0b' : '#ef4444';
  const label = rate >= 80 ? 'Great sprint!' : rate >= 50 ? 'On track' : 'Needs attention';

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-16 h-16 shrink-0">
        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={r} fill="none" stroke="#f1f5f9" strokeWidth="8" />
          <circle
            cx="32" cy="32" r={r} fill="none"
            stroke={color} strokeWidth="8"
            strokeDasharray={circ} strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.6s ease' }}
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-slate-800">
          {rate}%
        </span>
      </div>
      <div>
        <p className="text-base font-semibold text-slate-800">Completion Rate</p>
        <p className="text-xs mt-0.5" style={{ color }}>{label}</p>
      </div>
    </div>
  );
};

const StatBar: React.FC<{ label: string; count: number; total: number; color: string }> = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-600 font-medium">{label}</span>
        <span className="font-semibold text-slate-800">{count} <span className="text-slate-400 font-normal">({pct}%)</span></span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

const AssigneeRow: React.FC<{ name: string; done: number; total: number; avatar?: string }> = ({ name, done, total, avatar }) => {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div className="flex items-center gap-3">
      {avatar ? (
        <img src={avatar} alt={name} className="w-7 h-7 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-bold shrink-0">
          {initials}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-xs font-medium text-slate-700 truncate">{name}</span>
          <span className="text-xs text-slate-500 shrink-0 ml-2">{done}/{total}</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#10b981' : '#7c3aed' }}
          />
        </div>
      </div>
    </div>
  );
};

const SprintReportView: React.FC<{
  report: SprintReport;
  teamId: string;
  sprints: JiraSprint[];
  selectedSprintId: number | null;
  loadingSprints: boolean;
  onSprintSelect: (id: number | null) => void;
}> = ({ report, teamId, sprints, selectedSprintId, loadingSprints, onSprintSelect }) => {
  const [showCarryOver, setShowCarryOver] = useState(false);

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Sprint selector */}
      <SprintSelector
        sprints={sprints}
        selectedSprintId={selectedSprintId}
        teamId={teamId}
        loading={loadingSprints}
        onAfterSelect={onSprintSelect}
      />

      <div className="px-4 pb-6 space-y-4">

        {/* 1 · Completion Rate */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <CompletionRing rate={report.completion_rate} />
        </div>

        {/* 2 · Sprint Health */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Sprint Health</p>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              { label: 'Done', count: report.done_count, color: '#10b981', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: '✓' },
              { label: 'In Progress', count: report.in_progress_count, color: '#f59e0b', bg: 'bg-amber-50', text: 'text-amber-700', icon: '⟳' },
              { label: 'To Do', count: report.todo_count, color: '#94a3b8', bg: 'bg-slate-50', text: 'text-slate-600', icon: '○' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
                <div className={`text-2xl font-bold ${s.text}`}>{s.count}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2.5">
            <StatBar label="Done" count={report.done_count} total={report.total} color="#10b981" />
            <StatBar label="In Progress" count={report.in_progress_count} total={report.total} color="#f59e0b" />
            <StatBar label="To Do" count={report.todo_count} total={report.total} color="#94a3b8" />
          </div>
        </div>

        {/* 3 · Team Workload */}
        {report.assignees.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Team Workload</p>
            <div className="space-y-3">
              {report.assignees.map(a => (
                <AssigneeRow key={a.name} name={a.name} done={a.done} total={a.total} avatar={a.avatar} />
              ))}
            </div>
          </div>
        )}

        {/* 4 · Issue Types */}
        {report.issue_types.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Issue Types</p>
            <div className="flex flex-wrap gap-2">
              {report.issue_types.map(t => (
                <div
                  key={t.name}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${ISSUE_TYPE_COLORS[t.name] || 'bg-slate-100 text-slate-600'}`}
                >
                  <span>{t.name}</span>
                  <span className="font-bold opacity-70">{t.count}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 h-2.5 rounded-full overflow-hidden flex gap-0.5">
              {report.issue_types.map(t => (
                <div
                  key={t.name}
                  title={`${t.name}: ${t.count}`}
                  className="h-full transition-all duration-700"
                  style={{
                    width: `${(t.count / report.total) * 100}%`,
                    backgroundColor:
                      t.name === 'Bug' ? '#ef4444' :
                      t.name === 'Story' ? '#3b82f6' :
                      t.name === 'Task' ? '#8b5cf6' :
                      t.name === 'Epic' ? '#a855f7' : '#94a3b8',
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* 5 · Carry-over Items */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setShowCarryOver(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Carry-over Items</span>
              {report.carry_over.length > 0 && (
                <span className="bg-red-100 text-red-600 text-xs font-bold rounded-full px-1.5 py-0.5">
                  {report.carry_over.length}
                </span>
              )}
            </div>
            <svg
              className={`w-4 h-4 text-slate-400 transition-transform ${showCarryOver ? 'rotate-180' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showCarryOver && (
            <div className="border-t border-slate-100 px-3 pb-3 pt-2 space-y-2">
              {report.carry_over.length === 0 ? (
                <div className="text-center py-4">
                  <span className="text-2xl">🎉</span>
                  <p className="text-sm text-slate-500 mt-1">All issues completed!</p>
                </div>
              ) : (
                report.carry_over.map(issue => (
                  <IssueCard key={issue.id} issue={issue} compact />
                ))
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

// ─── No Sprint Selected Prompt ────────────────────────────────────────────────

const SelectSprintPrompt: React.FC<{
  sprints: JiraSprint[];
  teamId: string;
  loading: boolean;
  onAfterSelect?: (id: number | null) => void;
}> = ({ sprints, teamId, loading, onAfterSelect }) => (
  <div className="flex-1 flex flex-col">
    <SprintSelector sprints={sprints} selectedSprintId={null} teamId={teamId} loading={loading} onAfterSelect={onAfterSelect} />

    <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3 pb-12">
      {loading ? (
        <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      ) : sprints.length === 0 ? (
        <>
          <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-slate-600 text-sm font-medium">No sprints found</p>
          <p className="text-slate-400 text-xs">Make sure your Jira board has sprints and your project key is correct.</p>
        </>
      ) : (
        <>
          <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-slate-600 text-sm font-medium">Select a sprint to see the report</p>
          <p className="text-slate-400 text-xs">Sprint health, team workload, and carry-over items will appear here.</p>
        </>
      )}
    </div>
  </div>
);

// ─── Main Panel ───────────────────────────────────────────────────────────────

const JiraPanel: React.FC<JiraPanelProps> = ({ isOpen, onClose, teamId, inline }) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('issues');
  const [selectedReportType, setSelectedReportType] = useState<ReportType | null>(null);
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return {
      start: start.toISOString().split('T')[0],
      end: now.toISOString().split('T')[0],
    };
  });

  const {
    integration,
    sprints,
    issues,
    selectedSprintId,
    boardId,
    sprintReport,
    velocityData,
    burnupData,
    deploymentFrequencyData,
    loadingIntegration,
    loadingSprints,
    loadingIssues,
    loadingReport,
    loadingVelocity,
    loadingBurnup,
    loadingDeploymentFrequency,
    error,
    fetchIntegration,
    fetchSprints,
    fetchIssues,
    fetchSprintReport,
    fetchVelocity,
    fetchBurnup,
    fetchDeploymentFrequency,
    reset,
  } = useJiraStore();

  const load = useCallback(async (tid: string) => {
    await fetchIntegration(tid);
  }, [fetchIntegration]);

  useEffect(() => {
    if (isOpen && teamId) load(teamId);
    if (!isOpen) reset();
  }, [isOpen, teamId, load, reset]);

  useEffect(() => {
    if (integration && teamId && isOpen) {
      fetchSprints(teamId).then(() => {
        const { sprints: loaded, selectSprint } = useJiraStore.getState();
        const active = loaded.find(s => s.state === 'active') ?? loaded[0] ?? null;
        if (active) {
          selectSprint(teamId, active.id);
        } else {
          fetchIssues(teamId);
        }
      });
    }
  }, [integration, teamId, isOpen, fetchSprints, fetchIssues]);

  const handleTabChange = useCallback((tab: ActiveTab) => {
    setActiveTab(tab);
    setSelectedReportType(null);
  }, []);

  const handleReportTypeSelect = useCallback((type: ReportType) => {
    setSelectedReportType(type);
    const { selectedSprintId: sid, sprints: sp, boardId: bid } = useJiraStore.getState();
    if (!teamId) return;

    if (type === 'burndown' && sid) {
      const sprint = sp.find(s => s.id === sid);
      fetchSprintReport(teamId, sid, sprint?.name);
    } else if (type === 'velocity' && bid) {
      fetchVelocity(teamId, bid);
    } else if (type === 'burnup' && sid) {
      fetchBurnup(teamId, sid);
    } else if (type === 'deployment_frequency' && sid) {
      fetchDeploymentFrequency(teamId, sid);
    }
  }, [teamId, fetchSprintReport, fetchVelocity, fetchBurnup, fetchDeploymentFrequency]);

  const notConfigured = !loadingIntegration && !integration;
  const selectedReportDef = selectedReportType ? REPORT_DEFS.find(d => d.id === selectedReportType) : null;

  const panelContent = (
    <>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-200 shrink-0">
        <JiraLogo />
        <span className="font-semibold text-slate-900 text-base flex-1">Jira</span>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Tab Bar */}
      {integration && !loadingIntegration && (
        <div className="flex gap-0 px-4 pt-3 pb-0 border-b border-slate-200 shrink-0">
          {(['issues', 'report'] as ActiveTab[]).map(tab => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`pb-2.5 px-3 text-sm font-semibold border-b-2 transition-colors capitalize -mb-px ${
                activeTab === tab
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {tab === 'issues' ? 'Issues' : 'Sprint Reports'}
            </button>
          ))}
        </div>
      )}

      {/* Report sub-header: shown when a report type is selected */}
      {activeTab === 'report' && selectedReportDef && (
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50 shrink-0">
          <button
            onClick={() => setSelectedReportType(null)}
            className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors shrink-0"
            aria-label="Back to report list"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-xs font-semibold text-slate-700 truncate">{selectedReportDef.name}</span>
          {!selectedReportDef.implemented && (
            <span className="ml-auto text-[10px] font-bold uppercase tracking-wide bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full shrink-0">
              Soon
            </span>
          )}
        </div>
      )}

      {/* Body */}
      <div className={`flex-1 overflow-y-auto flex flex-col min-h-0${inline ? ' pr-10' : ''}`}>
        {loadingIntegration && (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {notConfigured && (
          <div className="flex flex-col items-center justify-center h-64 px-6 text-center gap-3">
            <JiraLogo className="w-10 h-10 opacity-30" />
            <p className="text-slate-500 text-sm">Jira integration is not configured for this team.</p>
            <p className="text-slate-400 text-xs">
              Go to <strong className="text-slate-600">Team Settings</strong> to connect your Jira account.
            </p>
          </div>
        )}

        {error && !loadingIntegration && (
          <div className="mx-4 mt-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-red-600 text-sm">
            {error}
          </div>
        )}

        {integration && !loadingIntegration && (
          <>
            {/* Connected badge */}
            <div className="flex items-center gap-2 px-4 pt-3 pb-1 shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
              <span className="text-xs text-slate-500">{integration.domain} · {integration.project_key}</span>
            </div>

            {/* ── ISSUES TAB ── */}
            {activeTab === 'issues' && (
              <>
                {!loadingSprints && sprints.length > 0 && (
                  <SprintSelector sprints={sprints} selectedSprintId={selectedSprintId} teamId={teamId!} loading={loadingIssues} />
                )}
                <div className="px-4 pb-4 space-y-2 mt-1">
                  {loadingIssues && <IssueSkeleton />}
                  {!loadingIssues && issues.length === 0 && (
                    <div className="text-center text-slate-400 text-sm py-8">No issues found.</div>
                  )}
                  {!loadingIssues && issues.map(issue => (
                    <IssueCard key={issue.id} issue={issue} />
                  ))}
                </div>
              </>
            )}

            {/* ── REPORT TAB ── */}
            {activeTab === 'report' && (
              <>
                {/* Step 1: choose report type */}
                {!selectedReportType && (
                  <ReportTypeGrid onSelect={handleReportTypeSelect} />
                )}

                {/* Step 2a: Sprint burndown (implemented) */}
                {selectedReportType === 'burndown' && (
                  <>
                    {!selectedSprintId && (
                      <SelectSprintPrompt
                        sprints={sprints}
                        teamId={teamId!}
                        loading={loadingSprints}
                        onAfterSelect={id => {
                          if (id && teamId) {
                            const sprint = sprints.find(s => s.id === id);
                            fetchSprintReport(teamId, id, sprint?.name);
                          }
                        }}
                      />
                    )}
                    {selectedSprintId && (loadingReport || !sprintReport) && (
                      <div className="pt-4">
                        {!loadingSprints && sprints.length > 0 && (
                          <SprintSelector sprints={sprints} selectedSprintId={selectedSprintId} teamId={teamId!} loading={true} />
                        )}
                        <ReportSkeleton />
                      </div>
                    )}
                    {selectedSprintId && sprintReport && !loadingReport && (
                      <SprintReportView
                        report={sprintReport}
                        teamId={teamId!}
                        sprints={sprints}
                        selectedSprintId={selectedSprintId}
                        loadingSprints={loadingSprints}
                        onSprintSelect={id => {
                          if (id && teamId) {
                            const sprint = sprints.find(s => s.id === id);
                            fetchSprintReport(teamId, id, sprint?.name);
                          }
                        }}
                      />
                    )}
                  </>
                )}

                {/* Step 2b: Velocity report */}
                {selectedReportType === 'velocity' && (
                  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                    {loadingVelocity && <ChartSkeleton />}
                    {!loadingVelocity && !velocityData && (
                      <div className="text-center text-slate-400 text-sm py-10">
                        {boardId
                          ? 'No velocity data available.'
                          : 'No board found for this project. Select a sprint first.'}
                      </div>
                    )}
                    {!loadingVelocity && velocityData && <VelocityReport data={velocityData} />}
                  </div>
                )}

                {/* Step 2c: Burnup report */}
                {selectedReportType === 'burnup' && (
                  <>
                    {!selectedSprintId && (
                      <SelectSprintPrompt
                        sprints={sprints}
                        teamId={teamId!}
                        loading={loadingSprints}
                        onAfterSelect={id => {
                          if (id && teamId) fetchBurnup(teamId, id);
                        }}
                      />
                    )}
                    {selectedSprintId && (
                      <div className="flex-1 overflow-y-auto">
                        <SprintSelector
                          sprints={sprints}
                          selectedSprintId={selectedSprintId}
                          teamId={teamId!}
                          loading={loadingSprints}
                          onAfterSelect={id => { if (id && teamId) fetchBurnup(teamId, id); }}
                        />
                        <div className="px-4 pb-6 space-y-4">
                          {loadingBurnup && <ChartSkeleton />}
                          {!loadingBurnup && burnupData && <BurnupReport data={burnupData} />}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Step 2d: Deployment Frequency report */}
                {selectedReportType === 'deployment_frequency' && (
                  <>
                    {!selectedSprintId && (
                      <SelectSprintPrompt
                        sprints={sprints}
                        teamId={teamId!}
                        loading={loadingSprints}
                        onAfterSelect={id => {
                          if (id && teamId) fetchDeploymentFrequency(teamId, id);
                        }}
                      />
                    )}
                    {selectedSprintId && (
                      <div className="flex-1 overflow-y-auto">
                        <SprintSelector
                          sprints={sprints}
                          selectedSprintId={selectedSprintId}
                          teamId={teamId!}
                          loading={loadingSprints}
                          onAfterSelect={id => { if (id && teamId) fetchDeploymentFrequency(teamId, id); }}
                        />
                        <div className="px-4 pb-6 space-y-4">
                          {loadingDeploymentFrequency && <ChartSkeleton />}
                          {!loadingDeploymentFrequency && deploymentFrequencyData && (
                            <DeploymentFrequencyReport data={deploymentFrequencyData} />
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Step 2e: Sprint-based reports still coming soon (none left, but guard for safety) */}
                {selectedReportType && !['burndown', 'velocity', 'burnup', 'deployment_frequency'].includes(selectedReportType) && selectedReportDef?.filterType === 'sprint' && (
                  <div className="flex flex-col flex-1">
                    <SprintSelector
                      sprints={sprints}
                      selectedSprintId={selectedSprintId}
                      teamId={teamId!}
                      loading={loadingSprints}
                    />
                    <ComingSoonPlaceholder reportName={selectedReportDef.name} />
                  </div>
                )}

                {/* Step 2c: Date range reports (coming soon) */}
                {selectedReportType && selectedReportDef?.filterType === 'date_range' && (
                  <div className="flex flex-col flex-1">
                    <div className="px-4 pt-3 pb-1">
                      <p className="text-xs font-medium text-slate-500 mb-2">Date range</p>
                    </div>
                    <DateRangeFilter
                      startDate={dateRange.start}
                      endDate={dateRange.end}
                      onChange={(start, end) => setDateRange({ start, end })}
                    />
                    <ComingSoonPlaceholder reportName={selectedReportDef.name} />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      {integration && !loadingIntegration && activeTab === 'issues' && (
        <div className="shrink-0 border-t border-slate-200 px-4 py-3 flex items-center justify-between">
          <span className="text-xs text-slate-400">
            {issues.length} issue{issues.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => teamId && fetchIssues(teamId, selectedSprintId ?? undefined)}
            disabled={loadingIssues}
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <svg className={`w-3.5 h-3.5 ${loadingIssues ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      )}

      {integration && !loadingIntegration && activeTab === 'report' && selectedReportType === 'burndown' && sprintReport && !loadingReport && (
        <div className="shrink-0 border-t border-slate-200 px-4 py-3 flex items-center justify-between">
          <span className="text-xs text-slate-400">
            {sprintReport.sprint_name || `Sprint #${sprintReport.sprint_id}`}
          </span>
          <button
            onClick={() => {
              if (teamId && selectedSprintId) {
                const sprint = sprints.find(s => s.id === selectedSprintId);
                fetchSprintReport(teamId, selectedSprintId, sprint?.name);
              }
            }}
            disabled={loadingReport}
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <svg className={`w-3.5 h-3.5 ${loadingReport ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      )}
    </>
  );

  if (inline) {
    return <div className="h-full flex flex-col bg-white">{panelContent}</div>;
  }

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />}
      <div
        className={`fixed top-0 right-0 h-full w-[460px] max-w-full bg-white shadow-2xl z-50
          transition-transform duration-300 ease-in-out flex flex-col border-l border-slate-200
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {panelContent}
      </div>
    </>
  );
};

export default JiraPanel;
